import React, { useCallback } from 'react';
import Modal from './Modal';
import { bookingService } from '../../services';
import { useWorkflowAgent } from '../../hooks';
import { setCurrentAgentName } from '../../services/api';

/**
 * Common workflow modal props interface
 */
export interface WorkflowModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback fired when modal should close */
  onClose: () => void;
  /** Hotel ID for API calls */
  hotelId: string;
  /** Current workflow session ID */
  workflowSessionId?: string;
  /** Current conversation ID */
  conversationId?: string;
  /** User's email address */
  userEmail: string;
  /** Agent name for workflow context */
  agentName?: string;
  /** Callback fired when workflow completes successfully */
  onComplete?: (data: any) => void;
  /** Modal content */
  children: React.ReactNode;
  /** Modal title */
  title?: string;
  /** Custom header content */
  customHeader?: React.ReactNode;
  /** Footer content */
  footer?: React.ReactNode;
  /** Whether modal should be full size */
  fullSize?: boolean;
  /** Maximum width for modal content */
  maxWidth?: string;
  /** Additional CSS classes */
  className?: string;
  /** Modal size variant */
  size?: string;
  /** Prevent closing modal */
  preventClose?: boolean;
  /** Whether to show close button */
  showCloseButton?: boolean;
  /** Whether to automatically exit workflow on close */
  autoExitWorkflow?: boolean;
  /** Callback to receive scroll function */
  onContentRefReady?: (scrollToTop: () => void) => void;
}

/**
 * WorkflowModal - Reusable wrapper for workflow-based modals
 * 
 * Handles common patterns like:
 * - Workflow exit API calls on close
 * - Standard modal props and layout
 * - Error handling for workflow operations
 * - Theme integration
 */
export const WorkflowModal: React.FC<WorkflowModalProps> = ({
  isOpen,
  onClose,
  hotelId,
  workflowSessionId,
  conversationId,
  userEmail,
  agentName,
  onComplete,
  children,
  title,
  customHeader,
  footer,
  fullSize = false,
  maxWidth = '4xl',
  className,
  showCloseButton = true,
  autoExitWorkflow = true,
  onContentRefReady
}) => {
  // Automatically set/clear global agent name for this workflow
  useWorkflowAgent(agentName);

  /**
   * Handle modal close with optional workflow exit
   */
  const handleClose = useCallback(async () => {
    console.log('🚪 WorkflowModal - handleClose called:', {
      autoExitWorkflow,
      conversationId,
      workflowSessionId,
      hotelId
    });
    
    // Only exit workflow if we have the required IDs and auto-exit is enabled
    if (autoExitWorkflow && conversationId && workflowSessionId) {
      try {
        console.log('🚪 WorkflowModal - Calling exit workflow API');
        await bookingService.exitWorkflowSafe(hotelId, {
          conversationId,
          workflowSessionId,
          guestEmail: userEmail
          // agent is automatically added by axios interceptor from global state
        });
        console.log('🚪 WorkflowModal - Exit workflow API completed');
      } catch (error) {
        console.warn('Failed to exit workflow safely:', error);
        // Don't prevent modal from closing even if API call fails
      }
    }

    // Clear the global agent name before closing
    setCurrentAgentName(null);

    if (!autoExitWorkflow || !conversationId || !workflowSessionId) {
      console.log('🚪 WorkflowModal - Skipping exit workflow API:', {
        reasons: {
          autoExitWorkflow: !autoExitWorkflow,
          noConversationId: !conversationId,
          noWorkflowSessionId: !workflowSessionId
        }
      });
    }
    
    console.log('🚪 WorkflowModal - Calling original onClose');
    onClose();
  }, [autoExitWorkflow, conversationId, workflowSessionId, hotelId, userEmail, agentName, onClose]);

  /**
   * Handle workflow completion
   */
  const handleComplete = useCallback((data: any) => {
    onComplete?.(data);
  }, [onComplete]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      customHeader={customHeader}
      footer={footer}
      fullSize={fullSize}
      maxWidth={maxWidth}
      className={className}
      showCloseButton={showCloseButton}
      onContentRefReady={onContentRefReady}
    >
      {children}
    </Modal>
  );
};

/**
 * Hook for workflow modal context
 * Provides common workflow-related data and functions to child components
 */
export interface WorkflowContextData {
  hotelId: string;
  workflowSessionId?: string;
  conversationId?: string;
  userEmail: string;
  agentName?: string;
  onComplete?: (data: any) => void;
}

// Context for sharing workflow data with child components
const WorkflowContext = React.createContext<WorkflowContextData | null>(null);

/**
 * WorkflowModalProvider - Provides workflow context to child components
 */
export const WorkflowModalProvider: React.FC<{
  children: React.ReactNode;
  value: WorkflowContextData;
}> = ({ children, value }) => (
  <WorkflowContext.Provider value={value}>
    {children}
  </WorkflowContext.Provider>
);

/**
 * Hook to access workflow context data
 */
export const useWorkflowContext = (): WorkflowContextData => {
  const context = React.useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflowContext must be used within a WorkflowModalProvider');
  }
  return context;
};

/**
 * Enhanced WorkflowModal with context provider
 */
/**
 * Enhanced WorkflowModal with context provider and footer support
 */
export const WorkflowModalWithContext: React.FC<WorkflowModalProps> = (props) => {
  const contextValue: WorkflowContextData = {
    hotelId: props.hotelId,
    workflowSessionId: props.workflowSessionId,
    conversationId: props.conversationId,
    userEmail: props.userEmail,
    agentName: props.agentName,
    onComplete: props.onComplete,
  };

  return (
    <WorkflowModal {...props}>
      <WorkflowModalProvider value={contextValue}>
        {props.children}
      </WorkflowModalProvider>
    </WorkflowModal>
  );
};